import api from "@helpers/api";

import { URLS } from "@constants/urls";

import {
  EducationType,
  LocalTrainingType,
  ForeignTrainingType,
  QualificationType,
  AdditionalProfessionalQualificationType,
  PublicationType,
  HonourType,
  RewardType,
} from "@type/HRM/qualification";

const apiEndPoint = `${process.env.REACT_APP_EMPLOYEE_SERVICE_URL}${process.env.REACT_APP_EMPLOYEE_SERVICE_PORT}/${process.env.REACT_APP_EMPLOYEE_SERVICE}/api/${process.env.REACT_APP_API_VERSION}/${process.env.REACT_APP_API_PRIVATE}/${URLS.EMPLOYEES}`;

export const getQualification = (() => {
  return async (data: {
    data: string;
  }): Promise<{ data: QualificationType }> => {
    const id = data.data; // employee id
    return api
      .get(`${apiEndPoint}/${id}/qualification-info`)
      .then((response) => ({ data: response.data }));
  };
})();

export const updateEducationInfo = (() => {
  return async (data: {
    data: EducationType;
  }): Promise<{ data: EducationType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/education-qualifications/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/education-qualifications`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const updateLocalTrainingInfo = (() => {
  return async (data: {
    data: LocalTrainingType;
  }): Promise<{ data: LocalTrainingType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/local-trainings/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/local-trainings`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const updateForeignTrainingInfo = (() => {
  return async (data: {
    data: ForeignTrainingType;
  }): Promise<{ data: ForeignTrainingType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/foreign-trainings/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/foreign-trainings`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const updateAdditionalProfessionalQualificationInfo = (() => {
  return async (data: {
    data: AdditionalProfessionalQualificationType;
  }): Promise<{ data: AdditionalProfessionalQualificationType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/professional-qualifications/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/professional-qualifications`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const updatePublicationInfo = (() => {
  return async (data: {
    data: PublicationType;
  }): Promise<{ data: PublicationType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/publications/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/publications`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const updateHonourInfo = (() => {
  return async (data: { data: HonourType }): Promise<{ data: HonourType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/honours/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/honours`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const updateRewardInfo = (() => {
  return async (data: { data: RewardType }): Promise<{ data: RewardType }> => {
    const { id, ...rest } = data.data;
    const { employeeID } = rest;

    const action = id ? "put" : "post";

    const putUrl = `${apiEndPoint}/${employeeID}/qualification-info/awards/${id}`;
    const postUrl = `${apiEndPoint}/${employeeID}/qualification-info/awards`;
    const url = id ? putUrl : postUrl;

    return api[action](url, { ...rest }).then((response) => ({
      data: response.data,
    }));
  };
})();

export const deleteEducationInfo = (() => {
  return async (data: {
    data: { employeeID: number | null; educationID: number };
  }): Promise<{ data: { id: number } }> => {
    const { employeeID, educationID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/education-qualifications/${educationID}`,
      )
      .then(() => ({ data: { id: educationID } }));
  };
})();

export const deleteLocalTrainingInfo = (() => {
  return async (data: {
    data: { employeeID: number | null; localTrainingID: number };
  }): Promise<{ data: { id: number } }> => {
    const { localTrainingID, employeeID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/local-trainings/${localTrainingID}`,
      )
      .then(() => ({ data: { id: localTrainingID } }));
  };
})();

export const deleteForeignTrainingInfo = (() => {
  return async (data: {
    data: { employeeID: number | null; foreignTrainingID: number };
  }): Promise<{ data: { id: number } }> => {
    const { foreignTrainingID, employeeID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/foreign-trainings/${foreignTrainingID}`,
      )
      .then(() => ({ data: { id: foreignTrainingID } }));
  };
})();

export const deleteAdditionalProfessionalQualificationInfo = (() => {
  return async (data: {
    data: {
      employeeID: number | null;
      additionalProfessionalQualificationID: number;
    };
  }): Promise<{ data: { id: number } }> => {
    const { additionalProfessionalQualificationID, employeeID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/professional-qualifications/${additionalProfessionalQualificationID}`,
      )
      .then(() => ({
        data: { id: additionalProfessionalQualificationID },
      }));
  };
})();

export const deletePublicationInfo = (() => {
  return async (data: {
    data: {
      employeeID: number | null;
      publicationID: number;
    };
  }): Promise<{ data: { id: number } }> => {
    const { publicationID, employeeID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/publications/${publicationID}`,
      )
      .then(() => ({ data: { id: publicationID } }));
  };
})();

export const deleteHonourInfo = (() => {
  return async (data: {
    data: {
      employeeID: number | null;
      honourID: number;
    };
  }): Promise<{ data: { id: number } }> => {
    const { honourID, employeeID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/honours/${honourID}`,
      )
      .then(() => ({ data: { id: honourID } }));
  };
})();

export const deleteRewardInfo = (() => {
  return async (data: {
    data: {
      employeeID: number | null;
      rewardID: number;
    };
  }): Promise<{ data: { id: number } }> => {
    const { rewardID, employeeID } = data.data;

    return api
      .delete(
        `${apiEndPoint}/${employeeID}/qualification-info/awards/${rewardID}`,
      )
      .then(() => ({ data: { id: rewardID } }));
  };
})();
